<template>
  <b-container class="mt-3">
    <template v-if="$apollo.queries.jobConnections.loading">
      Loading..
    </template>
    <template v-else-if="jobConnections">
      <div class="my-3 d-flex justify-content-end">
        <b-button-group>
          <b-button v-for="(where, label) in filterOptions" :key="label" @click.prevent="() => filterWhere = where"
            :variant="JSON.stringify(filterWhere) === JSON.stringify(where) ? 'primary' : 'outline-primary'">{{ label }}</b-button>
        </b-button-group>
      </div>
      <b-button-group v-if="isSelectable && !isReviewStep" class="mb-3">
        <b-button @click.prevent="batchEdit"
          variant="primary">Edit</b-button>
      </b-button-group>
      <b-table hover bordered :fields="fields" :items="items" ref="jobConnectionsTable" :selectable="isSelectable" select-mode="multi">
        <template #cell(title)="{ value, item: { id: jobID } }">
          <a href="#" @click.prevent="() => $router.push({ name: 'JobShowTaxonomy', params: { companyID, jobID } })">
            {{ value }}
          </a>
        </template>
        <template #cell(selected)="{ rowSelected }">
          <template v-if="rowSelected">
            <span aria-hidden="true">&check;</span>
            <span class="sr-only">Selected</span>
          </template>
          <template v-else>
            <span aria-hidden="true">&nbsp;</span>
            <span class="sr-only">Not selected</span>
          </template>
        </template>
      </b-table>
      <div class="d-flex flex-column align-items-center">
        <b-pagination v-model="filterPage" :per-page="filterLimit" :total-rows="jobConnections.total"/>
        <b-button-group v-if="isReviewStep">
          <b-button @click.prevent="() => $refs.jobConnectionsTable.selectedRows = $refs.jobConnectionsTable.localItems.map(() => true)"
            variant="primary">Select all</b-button>
          <b-button @click.prevent="bulkApprove" variant="success">Approve selected</b-button>
          <b-button @click.prevent="() => $refs.jobConnectionsTable.clearSelected()" variant="warning">Clear selected</b-button>
        </b-button-group>
      </div>
    </template>

    <b-modal v-if="jobID" visible size="xl" scrollable no-close-on-esc no-close-on-backdrop @hide="closeModal">
      <template v-slot:modal-title>
        <template v-if="$apollo.queries.selectedJob.loading">Loading..</template>
        <template v-else-if="selectedJob">Edit "{{ selectedJob.title }}" job
        </template>
      </template>
      <b-alert v-if="matchIfContactLocationNearJobLocation" show variant="warning">
        <font-awesome-icon class="mr-1" icon="map-marker"/>
        Contacts will be <strong>only be marked as "Match"</strong> if they are within 50mi of the job location.
      </b-alert>
      <template v-if="!$apollo.queries.selectedJob.loading && selectedJob">
        <b-row class="p-0">
          <b-col cols="12" md="6">
            <b-card :title="selectedJob.title" v-if="!this.jobsToEdit.length">
              <template v-if="selectedJob.location && selectedJob.location.name">
                <p>Location: {{selectedJob.location.name}} ({{selectedJob.location.countryCode}})</p>
              </template>
              <markdown v-if="!selectedJobShowFullDescription" :source="`${selectedJob.description.substring(0,500)}...`" :anchorAttributes="markdownAnchorAttributes"/>
              <markdown v-else :source="selectedJob.description" :anchorAttributes="markdownAnchorAttributes"/>
              <template v-slot:footer>
                <a href="#" @click.prevent="() => selectedJobShowFullDescription = !selectedJobShowFullDescription">
                  <template v-if="!selectedJobShowFullDescription">Show Full Description</template>
                  <template v-else>Hide Full Description</template>
                </a>
              </template>
            </b-card>
            <b-card v-else>
              <h3>{{multipleJobEditor}} Job(s) Selected</h3>
            </b-card>

            <b-card class="mt-3" v-if="!multipleJobEditor && selectedJob" no-body>
              <div class="card-header d-flex justify-content-between align-items-center bg-white">
                <h4 class="card-title m-0">Sovren</h4>
                <button class="close" @click="() => hideSovrenCard = !hideSovrenCard" type="button" v-if="selectedJob.sovren">
                  <font-awesome-icon icon="chevron-down" v-if="hideSovrenCard"/>
                  <font-awesome-icon icon="chevron-up" v-else/>
                </button>
                <div class="close" v-else>&times;</div>
              </div>
              <b-card-body v-if="!hideSovrenCard && selectedJob.sovren">
                <b-form-group label="Skills" v-if="typeof selectedJob.sovren.skills === 'string'">
                  <code>{{ JSON.parse(selectedJob.sovren.skills).join(', ') }}</code>
                  <!-- <multiselect :options="{}" :taggable="true" :value="JSON.parse(selectedJob.sovren.skills)"/> -->
                </b-form-group>
              </b-card-body>
            </b-card>

            <b-form-group v-if="!multipleJobEditor" class="mt-3" label="Locations" label-for="location">
              <multiselect
                v-model="matchingLocations"
                label="title"
                tag-placeholder="Add new location"
                placeholder="Search or add a location"
                track-by="id"
                :options="matchingLocationOptions"
                :multiple="true"
                :taggable="true"
                @tag="matchingLocationAddNew" />
            </b-form-group>
            <b-form-group v-if="!multipleJobEditor" label="Skills" label-for="location">
              <multiselect
                v-model="matchingSkills"
                label="title"
                tag-placeholder="Add new skill"
                placeholder="Search or add a skill"
                track-by="id"
                :options="matchingSkillOptions"
                :multiple="true"
                :taggable="true"
                @tag="matchingSkillAddNew" />
            </b-form-group>

            <b-table class="mt-3" show-empty striped :fields="matchesFields" :items="matchesResults"
              :current-page="matchesResultsPage" per-page="20">
                <template #cell(view)="{ toggleDetails, detailsShowing }">
                  <a href="#" @click.prevent="toggleDetails">
                    <font-awesome-icon :icon="detailsShowing ? 'eye-slash' : 'eye'"/>
                  </a>
                </template>
                <template #cell(record)="{ value: { companyName, title, city, country } }">
                  <template v-if="companyName && title">
                    <strong>{{ title }}</strong> at {{ companyName }}
                  </template>
                  <br v-if="companyName && title && city && country"/>
                  <small v-if="city && country">
                    <strong>{{ city }}</strong> ({{ country }})
                  </small>
                </template>
                <template #row-details="{ item: { entry } }">
                  <b-card v-if="entry && entry.id && Array.isArray(entry.employment)" :key="entry.id" body-class="p-2">
                    <b-card-text v-for="({ companyName, title }, i) in entry.employment" :key="i" :style="{ 'font-size': '12px' }">
                      <strong>{{ title }}</strong>, {{ companyName }}
                    </b-card-text>
                  </b-card>
                </template>
                <template #empty>
                  <p v-if="matchesLoading">Matches are loading.. ⏳</p>
                  <p v-else>There are no matches 😭</p>
                </template>
            </b-table>
            <b-pagination v-if="Array.isArray(matchesResults) && matchesResults.length > 20"
              v-model="matchesResultsPage" per-page="20" :total-rows="matchesResults.length"/>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Standardised Title *" label-for="standardised-title">
              <multiselect class="form-control p-0" id="standardised-title" :value="taxonomyTitleInput" @input="taxonomyTitleSelected"
                label="title" track-by="id" open-direction="bottom" :options="taxonomyTitleSearchResults"
                :loading="taxonomyTitleSearchLoading" :internal-search="false"
                :taggable="!taxonomyTitleSearchLoading" @tag="taxonomyTitleAddNew"
                :close-on-select="true" :options-limit="300" :limit="3" :limit-text="count => `and ${count} other titles`"
                :max-height="600" :show-no-results="true" @search-change="taxonomyTitleLookupSafe">
                  <template v-slot:option="{ option, search }">
                    <template v-if="option && option.id">
                      <template v-if="option.id.startsWith('es-')">👤 </template>
                      <template v-if="option.id.startsWith('new-')">➕ </template>
                      {{ option.title }}
                    </template>
                    <template v-else-if="search">➕ {{ search }}</template>
                  </template>
              </multiselect>
            </b-form-group>
            <b-form-group label="Matching Titles">
              <vue-draggable v-model="taxonomyRelatedList" handle=".input-group-prepend">
                <template v-for="(related, i) in taxonomyRelatedList">
                  <div v-if="related === matchesAboveKey" :key="`${i}-${related}`"
                    class="d-flex flex-row justify-content-start align-items-center my-2">
                      <div class="input-group-prepend">
                        <div class="input-group-text"><font-awesome-icon icon="bars"/></div>
                      </div>
                      <p class="my-0" :style="{ 'margin-left': '0.7rem' }">Matches above this line</p>
                  </div>
                  <b-input-group class="mb-2" v-else :key="`${i}-${related}`">
                    <template v-slot:prepend>
                      <b-input-group-text><font-awesome-icon icon="bars"/></b-input-group-text>
                    </template>
                    <b-form-input :id="`related-${i}`" :placeholder="getRandomTitle()"
                      :value="related" @input="value => taxonomyRelatedList[i] = value"
                      @keydown.meta.delete="() => taxonomyRelatedList.splice(i, 1)"
                      @keydown.meta.up="() => taxonomyRelatedMoveItem(true, related, i)"
                      @keydown.meta.down="() => taxonomyRelatedMoveItem(false, related, i)"/>
                    <b-input-group-append>
                      <b-button @click.prevent="() => taxonomyRelatedList.splice(i, 1)" tabindex="-1" variant="outline-danger">
                        <font-awesome-icon icon="minus"/>
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </template>
                <b-input-group>
                  <b-form-input disabled placeholder="Click + to add a related title"/>
                  <b-input-group-append>
                    <b-button @click.prevent="taxonomyRelatedAddNew" variant="outline-primary">
                      <font-awesome-icon icon="plus"/>
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </vue-draggable>
              <b-form-text>
                <p class="m-0">Keyboard shortcuts:</p>
                <ul class="m-0">
                  <li>{Tab} through related items</li>
                  <li>{⌘-Backspace} to remove related item</li>
                </ul>
              </b-form-text>
            </b-form-group>
            <b-form-group label="Ignored Titles" label-for="negativeTerms">
              <multiselect
                v-model="negativeTerms"
                label="title"
                tag-placeholder="Add a title that should be ignored"
                placeholder="Search or add a title"
                track-by="id"
                :options="negativeTermOptions"
                :multiple="true"
                :taggable="true"
                @tag="negativeTermAddNew"
                :disabled="!taxonomyTitleInput" />
            </b-form-group>
          </b-col>
        </b-row>
      </template>
      <template v-slot:modal-footer>
        <template v-if="multipleJobEditor">
          <div class="d-flex justify-content-between" v-if="selectedJob">
            <b-button class="m-1" @click="saveAndMatch" variant="info">Save &amp; Match</b-button>
            <b-button class="m-1" @click="closeModal" variant="primary">Close</b-button>
          </div>
        </template>
        <template v-else>
          <div class="d-flex justify-content-between" v-if="selectedJob">
            <b-button class="m-1" @click="saveData" tabindex="-1" variant="success">Save</b-button>
            <b-button class="m-1" @click="saveAndMatch" variant="info">Save &amp; Match</b-button>
            <b-button class="m-1" @click="saveAndNextModal" variant="primary">Save &amp; Next</b-button>
          </div>
        </template>
      </template>
    </b-modal>
  </b-container>
</template>

<script>
import _debounce from 'lodash/debounce';
import gql from 'graphql-tag';
import Markdown from 'vue-markdown';
import Multiselect from 'vue-multiselect';
import VueDraggable from 'vuedraggable';
import { mapRoot } from '@src/mixins/root';

const matchesAboveKey = '#MATCHES-ABOVE#';

const ListJobsQuery = gql`
query AdminJobTaxonomyToolListJobs(
  $filterWhere: JobWhereManyInput,
  $filterOrder: JobOrderByInput,
  $filterPage: Int = 1,
  $filterLimit: Int = 100,
) {
  company {
    id
    jobConnections(page: $filterPage, limit: $filterLimit, where: $filterWhere, order: $filterOrder) {
      edges {
        entry {
          id
          title
          location {
            id
            name
          }
          excerpt
          department {
            id
            name
          }
          standardised {
            id
            title
            related
            guessed
          }
          sovren {
            parsedAt
          }
          status
        }
      }
      page
      limit
      total
      totalPages
    }
    preferences {
      matchIfContactLocationNearJobLocation
    }
  }
}
`;

const GetOneJobQuery = gql`
query AdminJobTaxonomyToolGetOneJob($jobID: ID!) {
  company {
    id
    job(where: { id: $jobID }) {
      id
      title
      description
      location {
        id
        name
        countryCode
      }
      department {
        id
        name
      }
      standardised {
        id
        title
        related
        negativeTerms
      }
      matching {
        locations
        skills
      }
      sovren {
        result
        skills
        parsedAt
      }
      matches: contactConnections(where: { relevance_gte: 40, unsuitable: false }, order: RELEVANCE_DESC) {
        edges {
          entry {
            id
            employment {
              companyName
              title
            }
            locations {
              city
              country
              isPrimary
            }
          }
          relevance
        }
        total
      }
      status
    }
  }
}
`;

const LookupTaxonomyTitleQuery = gql`
query AdminJobTaxonomyToolLookupTitle($search: String!) {
  company {
    id
    jobTaxonomy(search: $search) {
      id
      title
      related
    }
  }
}
`;

const UpsertJobTaxonomyMutation = gql`
mutation AdminJobTaxonomyToolUpsert(
  $create: CreateJobTaxonomyInput,
  $update: UpdateJobTaxonomyInput,
  $where: JobTaxonomyWhereOneInput!
) {
  upsertJobTaxonomy(upsert: {
    create: $create,
    update: $update,
    where: $where,
  }) {
    id
    title
    related
    negativeTerms
  }
}
`;

const UpdateManyJobMutation = gql`
mutation AdminJobTaxonomyToolUpdateJobs($jobIDs: [ ID! ]!, $jobTaxonomyID: ID, $matching: JobMatchingBoostersInput) {
  updateManyJobs(update: { standardised: { id: $jobTaxonomyID }, matching: $matching }, where: { id_in: $jobIDs }) {
    id
    standardised {
      id
    }
  }
}
`;

const TriggerMatchingMutation = gql`
mutation AdminJobTaxonomyToolTriggerMatching($jobID: ID!) {
  matchContactsToJob(job: { id: $jobID }) {
    entry: contact {
      id
      employment {
        companyName
        title
      }
      locations {
        city
        country
        isPrimary
      }
    }
    relevance
  }
}
`;

const BulkApproveJobsMutation = gql`
mutation AdminJobTaxonomyToolBulkApproveJobs($upsert: [ UpsertJobInput! ]!) {
  upsertManyJobs(upsert: $upsert) {
    id
    title
    standardised {
      id
      title
      related
      guessed
    }
    status
  }
}
`;

export default {
  props: {
    companyID: String,
    jobID: {
      type: String,
      default: () => null,
    },
  },
  components: {
    Markdown,
    Multiselect,
    VueDraggable,
  },
  data: () => ({
    filterPage: 1,
    filterLimit: 100,
    filterWhere: {},
    filterOrder: 'TITLE_ASC',
    matchIfContactLocationNearJobLocation: null,
    selectedJobShowFullDescription: false,
    taxonomyTitleInput: null,
    taxonomyTitleSearchLoading: false,
    taxonomyTitleSearchResults: [],
    taxonomyRelatedList: [],
    hideSovrenCard: true,
    matchesLoading: false,
    matchesResults: [],
    matchesResultsPage: 1,
    jobsToEdit: [],
    matchingLocationOptions: [],
    matchingLocations: [],
    matchingSkillOptions: [],
    matchingSkills: [],
    negativeTerms: [],
    negativeTermOptions: []
  }),
  created() {
    this.filterWhere = this.filterOptions['To Review'];
    this.taxonomyTitleLookupSafe = _debounce(query => this.taxonomyTitleLookup(query), 700);
  },
  apollo: {
    jobConnections: {
      query: ListJobsQuery,
      variables() {
        const { companyID, filterPage, filterLimit, filterWhere, filterOrder } = this;
        return {
          companyID, // Technically unused, but forces a refetch when the company is changed
          filterPage,
          filterLimit,
          filterWhere,
          filterOrder,
        };
      },
      update: ({ company: { jobConnections } }) => jobConnections,
      result({ loading, data }) {
        if (!loading && data && data.company && data.company.id) {
          const { company } = data;

          const { jobConnectionsTable } = this.$refs;
          const isToReview = JSON.stringify(this.filterWhere) === JSON.stringify(this.filterOptions['To Review']);
          if (isToReview && jobConnectionsTable) {
            jobConnectionsTable.selectedRows = jobConnectionsTable.localItems.map(() => true);
          }

          if (company.preferences) {
            const { matchIfContactLocationNearJobLocation: value } = company.preferences;
            this.matchIfContactLocationNearJobLocation = value;
          }
        }
      },
      fetchPolicy: 'network-only',
    },
    selectedJob: {
      query: GetOneJobQuery,
      variables() {
        return { jobID: this.jobID };
      },
      update: ({ company: { job } }) => job,
      result({ loading, data }) {
        if (!loading && data && data.company && data.company.job) {
          const { title, standardised, matches, matching } = data.company.job;
          this.taxonomyTitleInput = standardised;
          this.taxonomyTitleSearchResults = [];
          this.taxonomyTitleLookup(title);
          this.selectedJobShowFullDescription = false;
          this.taxonomyRelatedList = [];

          if (standardised && Array.isArray(standardised.related) && standardised.related.length) {
            this.taxonomyRelatedList = standardised.related;
            if (!this.taxonomyRelatedList.includes(matchesAboveKey)) {
              this.taxonomyRelatedList.push(matchesAboveKey);
            }
          } else {
            this.taxonomyRelatedList = [];
          }

          if (standardised && Array.isArray(standardised.negativeTerms) && standardised.negativeTerms.length) {
            this.negativeTerms = standardised.negativeTerms.map(el => ({ title: el, id: `new-${Date.now()}-${el}` }));
            this.negativeTermOptions = JSON.parse(JSON.stringify(this.negativeTerms));
          }

          if (matching) {
            const { skills, locations } = matching;
            this.matchingLocations = Array.isArray(locations) ? locations.map(el => ({ title: el, id: `new-${Date.now()}-${el}` })) : [];
            this.matchingSkills = Array.isArray(skills) ? skills.map(el => ({ title: el, id: `new-${Date.now()}-${el}` })) : [];
            this.matchingLocationOptions = JSON.parse(JSON.stringify(this.matchingLocations));
            this.matchingSkillOptions = JSON.parse(JSON.stringify(this.matchingSkills));
          }

          if (matches && matches.edges) {
            this.matchesResults = matches.edges;
          } else {
            this.matchesResults = [];
          }
          this.matchesResultsPage = 1;
        }
      },
      skip() {
        return !this.jobID;
      },
      fetchPolicy: 'network-only',
    },
  },
  metaInfo() {
    const meta = {
      title: 'Jobs Taxonomy',
    };

    if (this.jobID) {
      meta.title = `Edit Job - ${meta.title}`;
    }

    return meta;
  },
  computed: {
    ...mapRoot([ 'company', 'user', 'isLoggedIn' ]),
    filterOptions: () => ({
      'All Jobs': { status: 'ACTIVE' },
      'To Review': { standardised: { guessed: true }, status: 'ACTIVE' },
      'No AI done': { standardised: null, status: 'ACTIVE' },
    }),
    multipleJobEditor() {
      return this.jobsToEdit && this.jobsToEdit.length;
    },
    items() {
      const { jobConnections } = this;
      if (jobConnections && Array.isArray(jobConnections.edges)) {
        return jobConnections.edges.map(({ entry }) => entry);
      } else {
        return [];
      }
    },
    fields() {
      const fields = [];

      if (this.isSelectable) {
        fields.push({
          label: '✓',
          key: 'selected',
          style: { width: '30px' },
        });
      }

      fields.push({
        label: 'Title',
        key: 'title',
      }, {
        label: 'Matched',
        key: 'standardised',
        formatter(value) {
          if (value) {
            const { title, related } = value;
            return `${title} ${Array.isArray(related) ? `(+${related.length})` : ''}`.trim();
          } else {
            return '(None)';
          }
        },
      });

      fields.push({
        label: 'Status',
        key: 'status',
      });

      return fields;
    },
    markdownAnchorAttributes: () => ({
      // Open all hyperlinks in the Markdown block in a new tab
      target: '_blank',
      // Enforce a nofollow to avoid SEO backlash from private content
      rel: 'nofollow',
    }),
    matchesFields: () => ([
      { label: '', key: 'view' },
      {
        label: 'Record',
        key: 'record',
        formatter(value, key, { entry: { employment, locations } }) {
          let row = {};

          if (Array.isArray(employment) && employment.length) {
            const [ { companyName, title } ] = employment;
            row = { ...row, companyName, title };
          }

          if (Array.isArray(locations) && locations.length) {
            const { city, country } = locations.find(l => l && l.isPrimary) || locations[0];
            row = { ...row, city, country };
          }

          return row;
        },
      },
      {
        label: 'Relevance',
        key: 'relevance',
        formatter: value => `${value || 0}%`,
      },
    ]),
    isSelectable() {
      return (JSON.stringify(this.filterWhere) === JSON.stringify(this.filterOptions['To Review'])) ||
      (JSON.stringify(this.filterWhere) === JSON.stringify(this.filterOptions['No AI done']));
    },
    isReviewStep() {
      return (JSON.stringify(this.filterWhere) === JSON.stringify(this.filterOptions['To Review']));
    },
    matchesAboveKey: () => matchesAboveKey,
  },
  methods: {
    getRandomTitle() {
      return (titles => titles[Math.floor(Math.random() * titles.length)])([
        'Enter a related title here',
        'Show me what you got, I want to see what you got',
        'When is a junior title not a related title? When it\'s an intern title',
        'Knock knock, who\'s there, related title, related title who?',
        'Have you ever had a dream Neo, what if you were unable to wake up from that dream?',
      ]);
    },
    async taxonomyTitleLookup(search) {
      try {
        this.taxonomyTitleSearchLoading = true;

        if (search) {
          const { data, errors } = await this.$apollo.query({
            query: LookupTaxonomyTitleQuery,
            variables: { search },
            fetchPolicy: 'network-only',
          });

          if (Array.isArray(errors) && errors.length) {
            errors.forEach(err => this.$log.error(err));
            throw errors[0];
          }

          if (data && data.company && Array.isArray(data.company.jobTaxonomy)) {
            this.taxonomyTitleSearchResults = data.company.jobTaxonomy;
          } else {
            this.taxonomyTitleSearchResults = [];
          }
        }
      } catch (err) {
        this.$notifyErr(err, { title: 'Failed to lookup taxonomy title' });
      } finally {
        this.taxonomyTitleSearchLoading = false;
      }
    },
    async triggerMatching() {
      try {
        this.matchesLoading = true;
        this.matchesResults = [];
        this.matchesResultsPage = 1;

        this.$notification({
          title: 'Matching triggered',
          message: `"${this.selectedJob.title}" has been submitted for matching`,
          variant: 'info',
        });

        const { data, errors } = await this.$apollo.mutate({
          mutation: TriggerMatchingMutation,
          variables: { jobID: this.jobID },
        });

        if (Array.isArray(errors) && errors.length) {
          errors.forEach(err => this.$log.error(err));
          throw errors[0];
        }

        if (data && Array.isArray(data.matchContactsToJob)) {
          this.matchesResults = data.matchContactsToJob;
        } else {
          this.matchesResults = [];
        }

        this.$notification({
          title: 'Matching finished',
          message: `"${this.selectedJob.title}" has been finished matching`,
          variant: 'success',
        });
      } catch (err) {
        this.$notifyErr(err, { title: 'Failed to finish matching for job' });
      } finally {
        this.matchesLoading = false;
      }
    },
    taxonomyTitleAddNew(input) {
      const tag = { id: `new-${Date.now()}`, title: input, related: [] };
      this.taxonomyTitleInput = tag;
      this.taxonomyTitleSearchResults.splice(0, 0, tag);
      this.taxonomyRelatedList = [];
    },
    matchingLocationAddNew(input) {
      const tag = { id: `new-${Date.now()}`, title: input };
      this.matchingLocationOptions.push(tag);
      this.matchingLocations.push(tag);
    },
    matchingSkillAddNew(input) {
      const tag = { id: `new-${Date.now()}`, title: input };
      this.matchingSkillOptions.push(tag);
      this.matchingSkills.push(tag);
    },
    negativeTermAddNew(input) {
      const tag = { id: `new-${Date.now()}`, title: input };
      this.negativeTermOptions.push(tag);
      this.negativeTerms.push(tag);
    },
    taxonomyRelatedMoveItem(up, related, i) {
      if (i === 0 && up) {
        return false;
      }
      if (i === this.taxonomyRelatedList.length - 1 && !up) {
        return false;
      }

      const j = up ? i - 1 : i + 1;
      this.taxonomyRelatedList.splice(i, 1);
      this.taxonomyRelatedList.splice(j, 0, related);
      this.$nextTick(() => {
        const input = this.$el.querySelector(`input#related-${j}`);
        input && input.focus();
      });
    },
    taxonomyRelatedAddNew() {
      this.taxonomyRelatedList.push('');
      if (!this.taxonomyRelatedList.includes(matchesAboveKey)) {
        this.taxonomyRelatedList.push(matchesAboveKey);
      }
      this.$nextTick(() => {
        const input = this.$el.querySelector(`input#related-${this.taxonomyRelatedList.length - 1}`);
        input && input.focus();
      });
    },
    taxonomyTitleSelected(row) {
      this.taxonomyTitleInput = row;
      this.taxonomyRelatedList = this.taxonomyRelatedList.concat(row && Array.isArray(row.related) ? row.related : []);
      if (!this.taxonomyRelatedList.includes(matchesAboveKey)) {
        this.taxonomyRelatedList.push(matchesAboveKey);
      }
    },
    closeModal() {
      if (this.multipleJobEditor) {
        this.jobsToEdit = [];
      }

      if (this.jobID) {
        const { companyID } = this;
        this.$router.replace({ name: 'JobIndexTaxonomy', params: { companyID } });
        this.$apollo.queries.jobConnections.refresh();
      }
    },
    async saveData() {
      try {
        let jobTaxonomyID = null;

        if (this.taxonomyTitleInput) {
          const { id, title } = this.taxonomyTitleInput;
          const related = this.taxonomyRelatedList.slice();
          const negativeTerms = this.negativeTerms.map(term => (term.title));

          const { data, errors: upsertJobTaxonomyErrors } = await this.$apollo.mutate({
            mutation: UpsertJobTaxonomyMutation,
            variables: {
              create: {
                title,
                related,
                negativeTerms,
              },
              update: {
                title,
                related,
                negativeTerms,
              },
              where: {
                id,
              },
            },
          });

          if (Array.isArray(upsertJobTaxonomyErrors) && upsertJobTaxonomyErrors.length) {
            upsertJobTaxonomyErrors.forEach(err => this.$log.error(err));
            throw upsertJobTaxonomyErrors[0];
          }

          if (!data || !data.upsertJobTaxonomy || !data.upsertJobTaxonomy.id) {
            throw new Error('Failed to save taxonomy record');
          }

          jobTaxonomyID = data.upsertJobTaxonomy.id;
        }

        if (this.multipleJobEditor) {
          const { errors: updateJobErrors } = await this.$apollo.mutate({
            mutation: UpdateManyJobMutation,
            variables: {
              jobIDs: this.jobsToEdit,
              jobTaxonomyID,
            },
          });

          if (Array.isArray(updateJobErrors) && updateJobErrors.length) {
            updateJobErrors.forEach(err => this.$log.error(err));
            throw updateJobErrors[0];
          }
        } else {
          const { errors: updateJobErrors } = await this.$apollo.mutate({
            mutation: UpdateManyJobMutation,
            variables: {
              jobIDs: [ this.jobID ],
              jobTaxonomyID,
              matching: {
                locations: this.matchingLocations.map(el => (el.title)),
                skills: this.matchingSkills.map(el => (el.title))
              }
            },
          });

          if (Array.isArray(updateJobErrors) && updateJobErrors.length) {
            updateJobErrors.forEach(err => this.$log.error(err));
            throw updateJobErrors[0];
          }
        }

        this.$notification({
          title: 'Taxonomy saved',
          message: `"${this.selectedJob.title}" has been saved successfully`,
          variant: 'success',
        });

        return true;
      } catch (err) {
        this.$log.error(err);
        this.$notifyErr(err);
        return false;
      }
    },
    async bulkApprove() {
      try {
        if (!this.isSelectable) {
          return this.$notification({
            title: 'Cannot bulk-approve',
            message: 'You must be on the To Review table to bulk-approve',
            variant: 'warning',
          });
        }

        const toApproveIDs = this.getSelectedItems();

        this.$log.debug({ toApproveIDs });

        if (toApproveIDs.length === 0) {
          return this.$notification({
            title: 'Nothing to bulk-approve',
            message: 'Please select some items to bulk-approve',
            variant: 'warning',
          });
        }

        const taxonomyMap = this.jobConnections.edges.reduce((map, { entry }) => {
          if (entry && toApproveIDs.includes(entry.id)) {
            const { standardised: { id: taxonomyID } } = entry;
            map[entry.id] = taxonomyID;
          }
          return map;
        }, {});

        const { errors } = await this.$apollo.mutate({
          mutation: BulkApproveJobsMutation,
          variables: {
            upsert: toApproveIDs.map(jobID => ({
              update: { standardised: { id: taxonomyMap[jobID] } },
              where: { id: jobID },
            })),
          },
        });

        if (Array.isArray(errors) && errors.length) {
          errors.forEach(err => this.$log.error(err));
          throw errors[0];
        }

        this.$notification({
          title: 'Successfully bulk-approved taxonomy',
          message: `${toApproveIDs.length} jobs have been saved successfully`,
          variant: 'success',
        });

        this.filterPage = 1;
        this.$apollo.queries.jobConnections.refresh();
      } catch (err) {
        this.$notifyErr(err, { title: 'Failed to bulk-approve' });
      }
    },
    async saveAndMatch() {
      try {
        await this.saveData();
        await this.triggerMatching();
      } catch (err) {
        this.$log.error(err);
        this.$notifyErr(err);
        return false;
      }
    },
    async saveAndNextModal() {
      try {
        // If saveData returned false, don't progress onwards
        if ((await this.saveData()) === true) {
          if (this.jobConnections && this.jobConnections.edges && this.jobConnections.edges.length) {
            const { companyID, jobID } = this;
            const currentIndex = this.jobConnections.edges
              .map(({ entry: { id: entryID } }) => entryID)
              .indexOf(jobID);

            // Usually we would be worried if currentIndex === -1
            // However in this case we are always incrementing the "currentIndex" by +1
            // So in the -1 case, currentIndex + 1 === 0, which will be the first job in the results set 💪
            const { entry: { id: nextJobID } } = this.jobConnections.edges[currentIndex + 1];
            this.$router.push({ name: 'JobShowTaxonomy', params: { companyID, jobID: nextJobID } });
            this.$apollo.queries.jobConnections.refresh();
          } else {
            this.closeModal();
          }
        }
      } catch (err) {
        this.$log.error(err);
        this.$notifyErr(err);
        this.closeModal();
      }
    },
    getSelectedItems() {
      const { jobConnectionsTable } = this.$refs;
      if (jobConnectionsTable && jobConnectionsTable.localItems && jobConnectionsTable.selectedRows) {
        return jobConnectionsTable.selectedRows.reduce((results, checked, i) => {
          if (checked) {
            const { id: jobID } = jobConnectionsTable.localItems[i];
            results.push(jobID);
          }

          return results;
        }, []);
      } else {
        return [];
      }
    },
    batchEdit() {
      this.jobsToEdit = this.getSelectedItems();
      this.$router.push({ name: 'JobShowTaxonomy', params: { companyID: this.companyID, jobID: this.jobsToEdit[0] } });
      console.log(this.jobsToEdit);
    }
  },
  watch: {
    companyID(companyID) {
      if (this.jobID) {
        // If there was a job selected, and the companyID is changed from underneath us, remove the jobID
        this.$router.replace({ name: 'JobIndexTaxonomy', params: { companyID } });
      }

      this.filterPage = 1;
    },
    jobID() {
      this.taxonomyTitleInput = null;
      this.taxonomyRelatedList = [];
      this.matchingLocationOptions = [];
      this.matchingLocations = [];
      this.matchingSkillOptions = [];
      this.matchingSkills = [];
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/Admin/JobTaxonomyTool.vue